import { useEffect, useMemo, useState } from "react";

import { SearchableDropdown } from "../../ui/searchable-dropdown";
import blocks, { BlockCategoryList, BlockType } from "../Blocks";

import { isBlockAvailableInEnvironment } from "./blockAvailability";

import { BlockEnv } from ".";

interface BlockSelectorProps {
  block?: BlockType;
  onSelect: (block: BlockType) => void;
  disabled?: boolean;
  env?: BlockEnv;
  customTrigger?: React.ReactNode;
}

interface BlockSelectorItem {
  id: BlockType;
  label: BlockType;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
}

const getBlockSelectorItem = (blockType: BlockType): BlockSelectorItem => {
  const blockDefinition = blocks[blockType];
  const IconComponent = blockDefinition.icon;

  return {
    id: blockType,
    label: blockType,
    description: blockDefinition.short_description,
    icon: ({ className }) => <IconComponent className={className} />,
  };
};

const BlockSelector = ({
  block,
  customTrigger,
  onSelect,
  disabled = false,
  env,
}: BlockSelectorProps) => {
  const [selectedBlock, setSelectedBlock] = useState<BlockSelectorItem | undefined>(() =>
    block ? getBlockSelectorItem(block) : undefined,
  );

  useEffect(() => {
    setSelectedBlock(block ? getBlockSelectorItem(block) : undefined);
  }, [block]);

  const categories = useMemo(
    () =>
      BlockCategoryList.map(category => ({
        label: category,
        items: Object.entries(blocks)
          .filter(([_, blockDefinition]) => blockDefinition.category === category)
          .filter(([blockName]) => isBlockAvailableInEnvironment(blockName as BlockType, env))
          .sort(([, a], [, b]) => (a.weight || 999) - (b.weight || 999))
          .map(([blockName]) => getBlockSelectorItem(blockName as BlockType)),
      })),
    [env],
  );

  const handleSelect = (item: BlockSelectorItem | undefined) => {
    if (disabled || !item) {
      return;
    }

    setSelectedBlock(item);
    onSelect(item.id);
  };

  return (
    <SearchableDropdown
      customTrigger={customTrigger}
      categories={categories}
      selectedItem={selectedBlock}
      onSelect={handleSelect}
      triggerClassName="w-full"
      className="min-w-[350px]"
      readonly={disabled}
    />
  );
};

export default BlockSelector;
